import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assessment as AssessmentIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BarChart as BarChartIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Nature as NatureIcon,
  Article as ArticleIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as PriceIcon,
  ShoppingCart as ExpenseIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import TreeDataDisplay from '../components/TreeDataDisplay';
import NewsManagement from '../components/admin/NewsManagement';
import ClientManagement from '../components/admin/ClientManagement';

const AdminDashboard = () => {
  const { user, hasPermission, isAuthenticated } = useAuth();
  const { darkMode } = useTheme();
  const [selectedSection, setSelectedSection] = useState('overview');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    role: '',
    email: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [treeRecords, setTreeRecords] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Performance tracking state
  const [selectedClient, setSelectedClient] = useState('');
  const [performanceTab, setPerformanceTab] = useState(0);
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [performanceDialog, setPerformanceDialog] = useState(false);
  const [performanceFormData, setPerformanceFormData] = useState({
    id: '',
    clientId: '',
    date: '',
    month: '',
    year: '',
    yieldType: '',
    yieldAmount: '',
    yieldUnit: 'kg',
    expenseType: '',
    expenseAmount: '',
    profitAmount: '',
    notes: ''
  });
  const [loading, setLoading] = useState(true);

  // Load tree records and clients from API
  useEffect(() => {
    const loadData = async () => {
      try {
        // Import API services
        const { treeAPI, clientAPI } = await import('../services/ApiService');
        
        // Load tree records
        const treesData = await treeAPI.getAllTrees();
        setTreeRecords(treesData);
        
        // Load clients
        const clientsData = await clientAPI.getAllClients();
        setClients(clientsData);
        
        // Initialize empty performance records (to be expanded later with actual API)
        setPerformanceRecords([]);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Set default section to overview when component mounts
  useEffect(() => {
    // Set default section based on permissions
    if (hasPermission('can_scan')) {
      setSelectedSection('treedata');
    } else {
      setSelectedSection('overview');
    }
  }, [hasPermission]);

  if (!hasPermission('is_staff')) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to access the admin dashboard.
        </Typography>
      </Box>
    );
  }

  const handleSectionChange = (section) => {
    // Only allow superusers to access the news section
    if (section === 'news' && !hasPermission('is_superuser')) {
      setError('Only Super Users can access News & Articles management');
      return;
    }
    
    setSelectedSection(section);
    setError(''); // Clear any errors when changing sections
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      username: '',
      role: '',
      email: ''
    });
    setError('');
    setSuccess('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Simulate API call
    setTimeout(() => {
      if (dialogType === 'addUser') {
        setSuccess('User added successfully!');
        handleCloseDialog();
      } else if (dialogType === 'editUser') {
        setSuccess('User updated successfully!');
        handleCloseDialog();
      }
    }, 1000);
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Total Users
            </Typography>
            <Typography variant="h3" color="primary">
              25
            </Typography>
          </CardContent>
          <CardActions>
            <Button startIcon={<PeopleIcon />} onClick={() => handleSectionChange('users')}>View Users</Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tree Records
            </Typography>
            <Typography variant="h3" color="primary">
              {treeRecords.length}
            </Typography>
          </CardContent>
          <CardActions>
            <Button startIcon={<NatureIcon />} onClick={() => handleSectionChange('treedata')}>View Records</Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Clients
            </Typography>
            <Typography variant="h3" color="primary">
              {clients.length}
            </Typography>
          </CardContent>
          <CardActions>
            <Button startIcon={<BusinessIcon />} onClick={() => handleSectionChange('clients')}>Manage Clients</Button>
          </CardActions>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            <Typography variant="h3" color="primary">
              {treeRecords.filter(record => {
                const recordDate = new Date(record.timestamp);
                const today = new Date();
                return recordDate.toDateString() === today.toDateString();
              }).length}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Records added today
            </Typography>
          </CardContent>
          <CardActions>
            <Button startIcon={<RefreshIcon />} onClick={() => handleSectionChange('treedata')}>View Activity</Button>
          </CardActions>
        </Card>
      </Grid>
    </Grid>
  );

  const renderUserManagement = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">User Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('addUser')}
        >
          Add User
        </Button>
      </Box>
      <Paper>
        <List>
          <ListItem>
            <ListItemText
              primary="John Doe"
              secondary="Admin"
            />
            <IconButton color="primary" onClick={() => handleOpenDialog('editUser')}>
              <EditIcon />
            </IconButton>
            <IconButton color="error">
              <DeleteIcon />
            </IconButton>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Jane Smith"
              secondary="Worker"
            />
            <IconButton color="primary" onClick={() => handleOpenDialog('editUser')}>
              <EditIcon />
            </IconButton>
            <IconButton color="error">
              <DeleteIcon />
            </IconButton>
          </ListItem>
        </List>
      </Paper>
    </Box>
  );

  const renderTreeData = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Tree Data Management</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadIcon />}
        >
          Export Data
        </Button>
      </Box>
      <TreeDataDisplay />
    </Box>
  );
  
  const renderClientManagement = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Client Management</Typography>
      </Box>
      <ClientManagement />
    </Box>
  );

  const renderNewsManagement = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">News and Articles</Typography>
      </Box>
      <NewsManagement />
    </Box>
  );

  const renderPerformanceTracking = () => {
    // Handler for tab changes
    const handleTabChange = (event, newValue) => {
      setPerformanceTab(newValue);
    };

    // Handler for client selection
    const handleClientChange = (event) => {
      setSelectedClient(event.target.value);
    };

    // Handler for opening the performance record dialog
    const handleOpenPerformanceDialog = (record = null) => {
      const today = new Date();
      const month = today.getMonth() + 1;
      const year = today.getFullYear();

      if (record) {
        // Edit mode - pre-fill form with record data
        setPerformanceFormData({
          ...record
        });
      } else {
        // Add mode - initialize with defaults
        setPerformanceFormData({
          id: Date.now().toString(),
          clientId: selectedClient,
          date: today.toISOString().split('T')[0],
          month: month.toString(),
          year: year.toString(),
          yieldType: '',
          yieldAmount: '',
          yieldUnit: 'kg',
          expenseType: '',
          expenseAmount: '',
          profitAmount: '',
          notes: ''
        });
      }
      setPerformanceDialog(true);
    };

    // Handler for closing the dialog
    const handleClosePerformanceDialog = () => {
      setPerformanceDialog(false);
    };

    // Handler for form field changes
    const handlePerformanceFormChange = (e) => {
      const { name, value } = e.target;
      setPerformanceFormData(prev => ({
        ...prev,
        [name]: value
      }));
    };

    // Handler for saving performance records
    const handleSavePerformance = () => {
      // Validate form
      if (!performanceFormData.clientId || 
          (performanceTab === 0 && (!performanceFormData.yieldType || !performanceFormData.yieldAmount)) ||
          (performanceTab === 1 && (!performanceFormData.expenseType || !performanceFormData.expenseAmount)) ||
          (performanceTab === 2 && !performanceFormData.profitAmount)) {
        setError('Please fill all required fields');
        return;
      }

      let updatedRecords = [...performanceRecords];
      const recordIndex = updatedRecords.findIndex(record => record.id === performanceFormData.id);

      if (recordIndex >= 0) {
        // Update existing record
        updatedRecords[recordIndex] = performanceFormData;
      } else {
        // Add new record
        updatedRecords.push(performanceFormData);
      }

      // Save to state and localStorage
      setPerformanceRecords(updatedRecords);
      localStorage.setItem('performanceRecords', JSON.stringify(updatedRecords));
      
      // Show success message and close dialog
      setSuccess('Performance record saved successfully');
      handleClosePerformanceDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    };

    // Delete a performance record
    const handleDeletePerformance = (id) => {
      if (window.confirm('Are you sure you want to delete this record?')) {
        const updatedRecords = performanceRecords.filter(record => record.id !== id);
        setPerformanceRecords(updatedRecords);
        localStorage.setItem('performanceRecords', JSON.stringify(updatedRecords));
        setSuccess('Record deleted successfully');
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess('');
        }, 3000);
      }
    };

    // Get filtered records for the selected client and tab
    const getFilteredRecords = () => {
      if (!selectedClient) return [];
      
      return performanceRecords.filter(record => {
        if (record.clientId !== selectedClient) return false;
        
        if (performanceTab === 0) {
          return record.yieldType && record.yieldAmount;
        } else if (performanceTab === 1) {
          return record.expenseType && record.expenseAmount;
        } else if (performanceTab === 2) {
          return record.profitAmount;
        }
        
        return true;
      });
    };

    // Get client name by ID
    const getClientName = (clientId) => {
      const client = clients.find(c => c.id === clientId);
      return client ? client.name : 'Unknown Client';
    };

    // Format currency for display
    const formatCurrency = (amount) => {
      if (!amount) return 'RM 0.00';
      return `RM ${parseFloat(amount).toFixed(2)}`;
    };

    const filteredRecords = getFilteredRecords();

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Performance Tracking</Typography>
          
          {selectedClient && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenPerformanceDialog()}
            >
              Add Record
            </Button>
          )}
        </Box>
        
        {/* Client selection dropdown */}
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>Select Client</InputLabel>
          <Select
            value={selectedClient}
            onChange={handleClientChange}
            label="Select Client"
          >
            <MenuItem value="">
              <em>Select a client</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client.id} value={client.id}>
                {client.name} {client.companyName ? `(${client.companyName})` : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {selectedClient ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={performanceTab} 
                onChange={handleTabChange}
                variant="fullWidth"
              >
                <Tab 
                  icon={<MoneyIcon />} 
                  label="Yield Production" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<ExpenseIcon />} 
                  label="Expenses" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<PriceIcon />} 
                  label="Monthly Profit" 
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            
            {/* Yield Production Tab */}
            {performanceTab === 0 && (
              <Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Crop Type</TableCell>
                        <TableCell align="right">Yield Amount</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords.length > 0 ? (
                        filteredRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.yieldType}</TableCell>
                            <TableCell align="right">{record.yieldAmount}</TableCell>
                            <TableCell>{record.yieldUnit}</TableCell>
                            <TableCell>{record.notes}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenPerformanceDialog(record)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeletePerformance(record.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No yield records found. Add a new record to get started.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            
            {/* Expenses Tab */}
            {performanceTab === 1 && (
              <Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Expense Type</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords.length > 0 ? (
                        filteredRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{record.date}</TableCell>
                            <TableCell>{record.expenseType}</TableCell>
                            <TableCell align="right">{formatCurrency(record.expenseAmount)}</TableCell>
                            <TableCell>{record.notes}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenPerformanceDialog(record)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeletePerformance(record.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No expense records found. Add a new record to get started.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
            
            {/* Monthly Profit Tab */}
            {performanceTab === 2 && (
              <Box>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell>Year</TableCell>
                        <TableCell align="right">Profit Amount</TableCell>
                        <TableCell>Notes</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRecords.length > 0 ? (
                        filteredRecords.map((record) => (
                          <TableRow key={record.id}>
                            <TableCell>{record.month}</TableCell>
                            <TableCell>{record.year}</TableCell>
                            <TableCell align="right">{formatCurrency(record.profitAmount)}</TableCell>
                            <TableCell>{record.notes}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => handleOpenPerformanceDialog(record)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => handleDeletePerformance(record.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No profit records found. Add a new record to get started.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">
            Please select a client to view and manage performance data.
          </Alert>
        )}
        
        {/* Performance Record Dialog */}
        <Dialog 
          open={performanceDialog} 
          onClose={handleClosePerformanceDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {performanceFormData.id ? 'Edit Performance Record' : 'Add Performance Record'}
          </DialogTitle>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            <TextField
              margin="dense"
              name="date"
              label="Date"
              type="date"
              fullWidth
              variant="outlined"
              value={performanceFormData.date}
              onChange={handlePerformanceFormChange}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            
            {performanceTab === 0 && (
              <>
                <TextField
                  margin="dense"
                  name="yieldType"
                  label="Crop Type"
                  fullWidth
                  variant="outlined"
                  value={performanceFormData.yieldType}
                  onChange={handlePerformanceFormChange}
                  sx={{ mb: 2 }}
                  select
                >
                  <MenuItem value="Palm Oil">Palm Oil</MenuItem>
                  <MenuItem value="Durian">Durian</MenuItem>
                  <MenuItem value="Corn">Corn</MenuItem>
                  <MenuItem value="Rice">Rice</MenuItem>
                  <MenuItem value="Rubber">Rubber</MenuItem>
                  <MenuItem value="Cocoa">Cocoa</MenuItem>
                  <MenuItem value="Coconut">Coconut</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
                
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <TextField
                      margin="dense"
                      name="yieldAmount"
                      label="Yield Amount"
                      type="number"
                      fullWidth
                      variant="outlined"
                      value={performanceFormData.yieldAmount}
                      onChange={handlePerformanceFormChange}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      margin="dense"
                      name="yieldUnit"
                      label="Unit"
                      fullWidth
                      select
                      variant="outlined"
                      value={performanceFormData.yieldUnit}
                      onChange={handlePerformanceFormChange}
                    >
                      <MenuItem value="kg">kg</MenuItem>
                      <MenuItem value="ton">ton</MenuItem>
                      <MenuItem value="pieces">pieces</MenuItem>
                      <MenuItem value="bunches">bunches</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </>
            )}
            
            {performanceTab === 1 && (
              <>
                <TextField
                  margin="dense"
                  name="expenseType"
                  label="Expense Type"
                  fullWidth
                  variant="outlined"
                  value={performanceFormData.expenseType}
                  onChange={handlePerformanceFormChange}
                  sx={{ mb: 2 }}
                  select
                >
                  <MenuItem value="Fertilizer">Fertilizer</MenuItem>
                  <MenuItem value="Pesticide">Pesticide</MenuItem>
                  <MenuItem value="Labor">Labor</MenuItem>
                  <MenuItem value="Equipment">Equipment</MenuItem>
                  <MenuItem value="Transportation">Transportation</MenuItem>
                  <MenuItem value="Maintenance">Maintenance</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </TextField>
                
                <TextField
                  margin="dense"
                  name="expenseAmount"
                  label="Expense Amount (RM)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={performanceFormData.expenseAmount}
                  onChange={handlePerformanceFormChange}
                />
              </>
            )}
            
            {performanceTab === 2 && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      margin="dense"
                      name="month"
                      label="Month"
                      fullWidth
                      select
                      variant="outlined"
                      value={performanceFormData.month}
                      onChange={handlePerformanceFormChange}
                      sx={{ mb: 2 }}
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                        <MenuItem key={month} value={month.toString()}>
                          {new Date(2000, month - 1, 1).toLocaleString('default', { month: 'long' })}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="dense"
                      name="year"
                      label="Year"
                      fullWidth
                      variant="outlined"
                      value={performanceFormData.year}
                      onChange={handlePerformanceFormChange}
                      sx={{ mb: 2 }}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  margin="dense"
                  name="profitAmount"
                  label="Profit Amount (RM)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={performanceFormData.profitAmount}
                  onChange={handlePerformanceFormChange}
                />
              </>
            )}
            
            <TextField
              margin="dense"
              name="notes"
              label="Notes"
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              value={performanceFormData.notes}
              onChange={handlePerformanceFormChange}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePerformanceDialog}>Cancel</Button>
            <Button onClick={handleSavePerformance} variant="contained" color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  };

  const renderReports = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Reports and Analytics</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadIcon />}
        >
          Generate Report
        </Button>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sex Ratio Distribution
              </Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Chart placeholder
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Monthly Records
              </Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Chart placeholder
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  const renderSettings = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        System Settings
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Data Management
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" color="error" sx={{ mr: 2 }}>
                Clear All Data
              </Button>
              <Button variant="outlined">
                Backup Data
              </Button>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Application Settings
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" sx={{ mr: 2 }}>
                Update App
              </Button>
              <Button variant="outlined">
                Check for Updates
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // Admin sidebar menu items
  const menuItems = [
    { id: 'overview', label: 'Overview', icon: <BarChartIcon /> },
    { id: 'treedata', label: 'Tree Data', icon: <NatureIcon /> },
    { id: 'clients', label: 'Clients', icon: <BusinessIcon /> },
    { id: 'performance', label: 'Performance Tracking', icon: <AssessmentIcon /> },
    { id: 'users', label: 'Users', icon: <GroupIcon /> },
    { id: 'reports', label: 'Reports', icon: <BarChartIcon /> },
    { id: 'news', label: 'News & Articles', icon: <ArticleIcon />, requireSuperUser: true },
    { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Admin Panel
            </Typography>
            <List component="nav" sx={{ mt: 2 }}>
              {menuItems.map((item) => {
                // Skip items that require superuser if the user isn't a superuser
                if (item.requireSuperUser && !hasPermission('is_superuser')) {
                  return null;
                }
                
                return (
                  <ListItemButton
                    key={item.id}
                    selected={selectedSection === item.id}
                    onClick={() => handleSectionChange(item.id)}
                  >
                    <ListItemIcon>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper sx={{ p: 2 }}>
            {selectedSection === 'overview' && renderOverview()}
            {selectedSection === 'users' && renderUserManagement()}
            {selectedSection === 'treedata' && renderTreeData()}
            {selectedSection === 'clients' && renderClientManagement()}
            {selectedSection === 'performance' && renderPerformanceTracking()}
            {selectedSection === 'reports' && renderReports()}
            {selectedSection === 'news' && renderNewsManagement()}
            {selectedSection === 'settings' && renderSettings()}
          </Paper>
        </Grid>
      </Grid>

      {/* User Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>
          {dialogType === 'addUser' ? 'Add New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            id="username"
            name="username"
            label="Username"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.username}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="role"
            name="role"
            label="Role"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.role}
            onChange={handleChange}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="email"
            name="email"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={formData.email}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard; 